import { Observable, ReplaySubject, Subject, filter, from, of, take } from 'rxjs'
import { getBrowserInfo } from '@awork/_shared/functions/browser-info'
import { coerce, satisfies } from 'semver'
import { normalizeDateValue, normalizeSyncFilters } from './helpers'
import type { PersistedEntityMeta, PersistedEntityState, PersistedPlainStateMeta } from './persistStateMigration'
import {
  createRowRangesWithoutMeta,
  exportPersistedData,
  importPersistedData,
  isEmptyPersistedMetaRow,
  packPlainStateRows,
  readObjectStoreRows,
  requiresPersistStateDatabaseRebuild,
  unpackPlainStateRows
} from './persistStateMigration'
import {
  getConfiguredPersistedStoreNames,
  getConfiguredPersistedStoreDefinition,
  hasConfiguredPersistedStoreName,
  type PersistedStoreDefinition,
  PERSIST_STATE_DB_NAME,
  PERSIST_STATE_DB_VERSION,
  PERSIST_STATE_EMPTY_META_ROW,
  PERSIST_STATE_META_ROW_KEY
} from './persistStateSchema'

interface EntityChanges<Entity> {
  upserts?: Entity[]
  deletes?: string[]
  meta: PersistedEntityMeta
}

interface PlainStateChanges {
  upserts?: Record<string, unknown>
  deletes?: string[]
  meta?: PersistedPlainStateMeta
}

export class PersistState {
  static instance: PersistState
  private static testStoreDefinitions = new Map<string, PersistedStoreDefinition>()
  private static testDatabaseName: string | null = null
  private static readonly databaseNameOverrideStorageKey = 'awDatabaseNameOverride'
  private static openDatabaseTimeoutMs = 5000
  private static deleteDatabaseBlockedTimeoutMs = 5000

  private dbp: Promise<IDBDatabase>
  private ready: Promise<void>
  private onInit$ = new ReplaySubject<void>()
  private persisted$ = new Subject<string>()
  private restored$ = new Subject<string>()
  private restoredStoreNames = new Set<string>()
  private loggedWriteErrors = new Set<string>()
  private databaseName: string
  private readonly primaryDatabaseName: string
  private initializationError: unknown = null
  private initialized = false

  storage: PersistState | null

  /**
   * Clears the static instance so the next consumer creates a fresh adapter.
   * PersistState is not Angular-owned, so tests reset it through this method.
   */
  static resetInstance(): void {
    Reflect.deleteProperty(PersistState, 'instance')
  }

  /**
   * Creates the persistence adapter and starts IndexedDB initialization.
   */
  constructor() {
    PersistState.instance = this
    this.primaryDatabaseName = PersistState.getPrimaryDatabaseName()
    this.databaseName = PersistState.getPersistedDatabaseNameOverride() || this.primaryDatabaseName
    this.storage = PersistState.isIndexedDBCapable() ? this : null
    this.dbp = this.storage ? this.openDatabase() : Promise.resolve(null as unknown as IDBDatabase)
    this.ready = this.initializeStorage()
  }

  /**
   * Returns the singleton persistence adapter.
   * @returns {PersistState}
   */
  static init(): PersistState {
    if (PersistState.instance && PersistState.instance.databaseName === PersistState.getDatabaseName()) {
      return PersistState.instance
    }

    const persistState = new PersistState()

    void persistState.ready.finally(() => {
      persistState.initialized = true
      persistState.onInit$.next()
    })

    return persistState
  }

  /**
   * Registers additional persisted store definitions for tests.
   * @param {PersistedStoreDefinition[]} storeDefinitions
   */
  static registerStoreDefinitionsForTesting(storeDefinitions: PersistedStoreDefinition[]): void {
    storeDefinitions
      .filter(storeDefinition => !!storeDefinition?.name && !!storeDefinition?.type)
      .forEach(storeDefinition => {
        PersistState.testStoreDefinitions.set(storeDefinition.name, storeDefinition)
      })
  }

  /**
   * Clears test-only persisted store definitions.
   */
  static resetStoreDefinitionsForTesting(): void {
    PersistState.testStoreDefinitions.clear()
  }

  /**
   * Overrides the IndexedDB database name for tests.
   * @param {string | null} databaseName
   */
  static setDatabaseNameForTesting(databaseName: string | null): void {
    PersistState.testDatabaseName = databaseName
  }

  /**
   * Overrides the IndexedDB open-request timeout used during initialization for tests.
   * @param {number | null} timeoutMs
   */
  static setOpenDatabaseTimeoutForTesting(timeoutMs: number | null): void {
    PersistState.openDatabaseTimeoutMs = timeoutMs ?? 5000
  }

  /**
   * Overrides the blocked-delete timeout used while rebuilding IndexedDB for tests.
   * @param {number | null} timeoutMs
   */
  static setDeleteDatabaseBlockedTimeoutForTesting(timeoutMs: number | null): void {
    PersistState.deleteDatabaseBlockedTimeoutMs = timeoutMs ?? 5000
  }

  /**
   * Returns the complete configured persisted-store list.
   * @returns {string[]}
   */
  private static getConfiguredStoreNames(): string[] {
    return getConfiguredPersistedStoreNames(PersistState.getTestStoreDefinitions())
  }

  /**
   * Returns the configured store definition for one logical store.
   * @param {string} storeName
   * @returns {PersistedStoreDefinition | undefined}
   */
  private static getConfiguredStoreDefinition(storeName: string): PersistedStoreDefinition | undefined {
    return getConfiguredPersistedStoreDefinition(storeName, PersistState.getTestStoreDefinitions())
  }

  /**
   * Returns the registered test-only persisted store definitions.
   * @returns {PersistedStoreDefinition[]}
   */
  private static getTestStoreDefinitions(): PersistedStoreDefinition[] {
    return [...PersistState.testStoreDefinitions.values()]
  }

  /**
   * Returns the active IndexedDB database name.
   * @returns {string}
   */
  private static getDatabaseName(): string {
    return PersistState.getPersistedDatabaseNameOverride() || PersistState.testDatabaseName || PERSIST_STATE_DB_NAME
  }

  /**
   * Returns the primary production IndexedDB database name without recovery overrides.
   * @returns {string}
   */
  private static getPrimaryDatabaseName(): string {
    return PersistState.testDatabaseName || PERSIST_STATE_DB_NAME
  }

  /**
   * Reads the persisted recovery database-name override from local storage.
   * @returns {string | null}
   */
  private static getPersistedDatabaseNameOverride(): string | null {
    try {
      return localStorage.getItem(PersistState.databaseNameOverrideStorageKey) || null
    } catch (_) {
      return null
    }
  }

  /**
   * Persists or clears the recovery database-name override in local storage.
   * @param {string | null} databaseName
   */
  private static setPersistedDatabaseNameOverride(databaseName: string | null): void {
    try {
      if (databaseName) {
        localStorage.setItem(PersistState.databaseNameOverrideStorageKey, databaseName)

        return
      }

      localStorage.removeItem(PersistState.databaseNameOverrideStorageKey)
    } catch (_) {}
  }

  /**
   * Creates a fresh recovery database name when the primary database remains wedged.
   * @param {string} databaseName
   * @returns {string}
   */
  private static createRecoveryDatabaseName(databaseName: string): string {
    const recoveryId =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : String(Date.now())

    return `${databaseName}-recovery-v${PERSIST_STATE_DB_VERSION}-${recoveryId}`
  }

  /**
   * Determines whether a store name is part of the configured schema.
   * @param {string} storeName
   * @returns {boolean}
   */
  private static hasConfiguredStoreName(storeName: string): boolean {
    return hasConfiguredPersistedStoreName(storeName, PersistState.getTestStoreDefinitions())
  }

  /**
   * Determines whether IndexedDB persistence is supported in the current browser.
   * @returns {boolean}
   */
  static isIndexedDBCapable(): boolean {
    try {
      const browserInfo = getBrowserInfo()
      const isOldSafari = browserInfo.name === 'Safari' && !satisfies(coerce(browserInfo.version), '>=15.x')

      return window.indexedDB && !isOldSafari
    } catch (_) {
      return false
    }
  }

  // Plain-store API

  /**
   * Reads a plain persisted store and emits its restore event.
   * @param {string} key
   * @returns {Promise<T>}
   */
  async getItem<T>(key: string): Promise<T> {
    const t0 = performance.now()

    return this.whenReady(() => this.readPlainState<T>(key))
      .then(value => {
        this.restoredStoreNames.add(key)
        this.restored$.next(key)

        return value
      })
      .catch(error => {
        this.handleReadError(error)
        return undefined
      })
      .finally(() => this.logRestore(key, t0))
  }

  /**
   * Persists a plain signal-store snapshot.
   * @param {string} key
   * @param {unknown} value
   */
  setItem(key: string, value: unknown): void {
    const t0 = performance.now()

    void this.whenReady(async () => {
      await this.writePlainState(key, value)
      this.persisted$.next(key)
      this.logPersist(key, t0)
    })
  }

  /**
   * Persists row-level changes for a plain signal store.
   * @param {string} storeName
   * @param {PlainStateChanges} changes
   */
  applyPlainStateChanges(storeName: string, changes: PlainStateChanges): void {
    const t0 = performance.now()
    const upsertCount = Object.keys(changes.upserts || {}).length
    const deleteCount = changes.deletes?.length || 0
    const metaChanged = !!changes.meta

    from(
      this.whenReady(async () => {
        await this.applyPlainStateChangesInternal(storeName, changes)
        this.persisted$.next(storeName)
        this.logPersist(storeName, t0, `upserts=${upsertCount}, deletes=${deleteCount}, meta=${metaChanged}`)
      })
    ).subscribe({
      next: () => {},
      error: () => {}
    })
  }

  /**
   * Deletes all persisted rows for one logical store.
   * @param {string} key
   * @param {Date} commandDate
   * @returns {Observable<boolean>}
   */
  deleteItem(key: string, commandDate?: Date): Observable<boolean> {
    return from(this.whenReady(() => this.deleteStoreInternal(key, commandDate), false))
  }

  // Entity-store API

  /**
   * Reads an entity persisted store and emits its restore event.
   * @param {string} storeName
   * @returns {Promise<PersistedEntityState<Entity> | undefined>}
   */
  async getEntityState<Entity extends { id: string }>(
    storeName: string
  ): Promise<PersistedEntityState<Entity> | undefined> {
    const t0 = performance.now()

    return this.whenReady(() =>
      this.readEntityStore<Entity>(storeName).then(({ meta, entities }) => {
        this.restoredStoreNames.add(storeName)
        this.restored$.next(storeName)

        return this.buildPersistedEntityState(meta, entities)
      })
    )
      .catch(error => {
        this.handleReadError(error)
        return undefined
      })
      .finally(() => this.logRestore(storeName, t0))
  }

  /**
   * Persists row-level changes for an entity signal store.
   * @param {string} storeName
   * @param {EntityChanges<Entity>} changes
   * @returns {Promise<void>}
   */
  async applyEntityChanges<Entity extends { id: string }>(
    storeName: string,
    changes: EntityChanges<Entity>
  ): Promise<void> {
    const t0 = performance.now()
    const upsertCount = changes.upserts?.length || 0
    const deleteCount = changes.deletes?.length || 0

    return this.whenReady(async () => {
      await this.applyEntityChangesInternal(storeName, changes)
      this.persisted$.next(storeName)
      this.logPersist(storeName, t0, `upserts=${upsertCount}, deletes=${deleteCount}`)
    })
  }

  /**
   * Deletes all persisted rows for an entity store.
   * @param {string} storeName
   * @returns {Observable<boolean>}
   */
  deleteEntityStore(storeName: string): Observable<boolean> {
    return from(this.whenReady(() => this.deleteStoreInternal(storeName), false))
  }

  // Shared maintenance API

  /**
   * Deletes all persisted rows for multiple logical stores.
   * @param {string[]} keys
   * @param {Date} commandDate
   * @returns {Observable<boolean>}
   */
  deleteItems(keys: string[], commandDate?: Date): Observable<boolean> {
    return from(this.whenReady(() => this.deleteStoresInternal(keys, commandDate), false))
  }

  /**
   * Deletes all persisted rows for all configured logical stores.
   * @param {Date} commandDate
   * @returns {Observable<boolean>}
   */
  deleteAllItems(commandDate?: Date): Observable<boolean> {
    return from(this.whenReady(() => this.deleteAllStoresInternal(commandDate), false))
  }

  /**
   * Clears all configured persisted stores.
   * @returns {Observable<void>}
   */
  clear(): Observable<void> {
    return from(this.whenReady(() => this.clearAllStores()))
  }

  /**
   * Emits when IndexedDB initialization finishes.
   * @returns {Observable<void>}
   */
  onInit(): Observable<void> {
    return this.onInit$.asObservable()
  }

  /**
   * Returns the current initialization and persistence state for diagnostics.
   * @returns {{ initialized: boolean; enabled: boolean }}
   */
  getStatus(): { initialized: boolean; enabled: boolean } {
    return { initialized: this.initialized, enabled: !!this.storage }
  }

  /**
   * Logs a restore failure without store data or an error stack.
   * @param {string} storeName
   * @param {unknown} error
   */
  logRestoreError(storeName: string, error: unknown): void {
    this.logError('Restoring signal-store state failed.', {
      storeName,
      error: {
        name: error instanceof Error ? error.name : 'UnknownError',
        message: error instanceof Error ? error.message : 'Unknown restore error'
      }
    })
  }

  /**
   * Emits once when a given store finishes persisting.
   * @param {string} storeName
   * @returns {Observable<string>}
   */
  onPersisted(storeName: string): Observable<string> {
    return this.persisted$.pipe(
      filter(key => key === storeName),
      take(1)
    )
  }

  /**
   * Emits once when a given store has been restored, or for every restored store when omitted.
   * @param {string} storeName
   * @returns {Observable<string>}
   */
  onRestored(storeName?: string): Observable<string> {
    if (storeName && this.restoredStoreNames.has(storeName)) {
      return of(storeName)
    }

    return storeName
      ? this.restored$.pipe(
          filter(key => key === storeName),
          take(1)
        )
      : this.restored$.asObservable()
  }

  /**
   * Dumps all configured persisted stores into a JSON file.
   * @param {string} [filename='awork-indexeddb-dump.json']
   * @param {boolean} [download=true]
   * @returns {Observable<File | void>}
   */
  dumpToFile(filename = 'awork-indexeddb-dump.json', download = true): Observable<File | void> {
    return new Observable<File | void>(observer => {
      this.whenReady(async () => {
        const db = await this.dbp
        return this.buildDumpData(db)
      }, null)
        .then(dump => {
          if (!dump) {
            observer.error(this.initializationError || new Error('Persisted storage is disabled.'))

            return
          }

          const jsonString = JSON.stringify(dump, null, 2)
          const blob = new Blob([jsonString], { type: 'application/json' })

          if (!download) {
            observer.next(new File([blob], filename, { type: 'application/json' }))
            observer.complete()

            return
          }

          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')

          link.href = url
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
          observer.next()
          observer.complete()
        })
        .catch(error => observer.error(error))
    })
  }

  // Shared IndexedDB helpers

  /**
   * Rebuilds the IndexedDB schema when the stored version does not match the configured schema.
   * @returns {Promise<void>}
   */
  private async initializeStorage(): Promise<void> {
    if (!this.storage) {
      return Promise.resolve()
    }

    return this.dbp
      .then(async db => {
        if (!this.requiresDatabaseRebuild(db)) {
          await this.ensureConfiguredStoreMetadata(db)
          await this.tryRestorePrimaryDatabaseIfNeeded(db)

          return
        }

        const persistedData = await exportPersistedData(db, PersistState.getTestStoreDefinitions())

        db.close()
        await this.deleteDatabase(this.databaseName)

        this.dbp = this.openDatabase(PERSIST_STATE_DB_VERSION)

        const rebuiltDb = await this.dbp

        await importPersistedData(rebuiltDb, persistedData, PersistState.getConfiguredStoreNames())
        await this.ensureConfiguredStoreMetadata(rebuiltDb)
        await this.tryRestorePrimaryDatabaseIfNeeded(rebuiltDb)
      })
      .catch(async error => {
        this.logError('IndexedDB initialization failed.', {
          databaseName: this.databaseName,
          error: this.describeError(error)
        })

        if (await this.tryRecoverInitializationFailure(error)) {
          return
        }

        this.disablePersistence(error)
      })
  }

  /**
   * Waits for initialization before running an IndexedDB operation.
   * @param {() => Promise<T>} callback
   * @param {T} [fallbackValue=undefined as T]
   * @returns {Promise<T>}
   */
  private whenReady<T>(callback: () => Promise<T>, fallbackValue = undefined as T): Promise<T> {
    return this.ready.then(() => {
      if (!this.storage) {
        return fallbackValue
      }

      return callback()
    })
  }

  /**
   * Opens the IndexedDB database and creates configured object stores during upgrades.
   * @param {number} version
   * @returns {Promise<IDBDatabase>}
   */
  private openDatabase(version?: number): Promise<IDBDatabase> {
    return this.openDatabaseByName(this.databaseName, version)
  }

  /**
   * Opens the specified IndexedDB database and creates configured object stores during upgrades.
   * @param {string} databaseName
   * @param {number} version
   * @returns {Promise<IDBDatabase>}
   */
  private openDatabaseByName(databaseName: string, version?: number): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const openRequest = version ? indexedDB.open(databaseName, version) : indexedDB.open(databaseName)
      let openTimeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
        openTimeoutId = null
        settled = true
        reject(new Error(`Opening IndexedDB database "${databaseName}" timed out.`))
      }, PersistState.openDatabaseTimeoutMs)
      let settled = false

      const clearOpenTimeout = (): void => {
        if (!openTimeoutId) {
          return
        }

        clearTimeout(openTimeoutId)
        openTimeoutId = null
      }

      openRequest.onerror = errorEvent => {
        if (settled) {
          return
        }

        settled = true
        clearOpenTimeout()
        reject(openRequest.error)
      }

      openRequest.onblocked = event => {
        if (settled) {
          return
        }
      }

      openRequest.onsuccess = () => {
        const db = openRequest.result

        if (settled) {
          db.close()

          return
        }

        settled = true
        clearOpenTimeout()
        db.onversionchange = () => db.close()
        resolve(db)
      }

      openRequest.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = openRequest.result
        const upgradeTransaction = openRequest.transaction

        if (settled) {
          try {
            upgradeTransaction?.abort()
          } catch (_) {}

          return
        }

        if (event.oldVersion && event.oldVersion < 4) {
          window.indexedDB.deleteDatabase('awTest')
        }

        PersistState.getConfiguredStoreNames().forEach(storeName => {
          if (!db.objectStoreNames.contains(storeName)) {
            db.createObjectStore(storeName).put(PERSIST_STATE_EMPTY_META_ROW, PERSIST_STATE_META_ROW_KEY)

            return
          }

          const objectStore = openRequest.transaction?.objectStore(storeName)

          if (!objectStore) {
            return
          }

          const metaRequest = objectStore.get(PERSIST_STATE_META_ROW_KEY)

          metaRequest.onsuccess = () => {
            if (metaRequest.result === undefined) {
              objectStore.put(PERSIST_STATE_EMPTY_META_ROW, PERSIST_STATE_META_ROW_KEY)
            }
          }
        })
      }
    })
  }

  /**
   * Determines whether the IndexedDB schema differs from the configured schema.
   * @param {IDBDatabase} db
   * @returns {boolean}
   */
  private requiresDatabaseRebuild(db: IDBDatabase): boolean {
    return requiresPersistStateDatabaseRebuild(db, PersistState.getConfiguredStoreNames())
  }

  /**
   * Deletes an IndexedDB database.
   * @param {string} databaseName
   * @returns {Promise<void>}
   */
  private deleteDatabase(databaseName: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const deleteRequest = indexedDB.deleteDatabase(databaseName)
      let blockedTimeoutId: ReturnType<typeof setTimeout> | null = null
      let requestTimeoutId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
        requestTimeoutId = null
        settled = true
        reject(new Error(`Deleting IndexedDB database "${databaseName}" timed out.`))
      }, PersistState.deleteDatabaseBlockedTimeoutMs)
      let settled = false

      const clearBlockedTimeout = (): void => {
        if (!blockedTimeoutId) {
          return
        }

        clearTimeout(blockedTimeoutId)
        blockedTimeoutId = null
      }

      const clearRequestTimeout = (): void => {
        if (!requestTimeoutId) {
          return
        }

        clearTimeout(requestTimeoutId)
        requestTimeoutId = null
      }

      deleteRequest.onsuccess = () => {
        if (settled) {
          return
        }

        settled = true
        clearBlockedTimeout()
        clearRequestTimeout()
        resolve()
      }
      deleteRequest.onerror = () => {
        if (settled) {
          return
        }

        settled = true
        clearBlockedTimeout()
        clearRequestTimeout()
        reject(deleteRequest.error)
      }
      deleteRequest.onblocked = () => {
        if (settled) {
          return
        }

        if (!blockedTimeoutId) {
          blockedTimeoutId = setTimeout(() => {
            if (settled) {
              return
            }

            settled = true
            clearRequestTimeout()

            const error = new Error(`Deleting IndexedDB database "${databaseName}" remained blocked.`)

            reject(error)
          }, PersistState.deleteDatabaseBlockedTimeoutMs)
        }
      }
    })
  }

  /**
   * Disables persistence after initialization failed so callers do not wait forever.
   * @param {unknown} error
   */
  private disablePersistence(error: unknown): void {
    this.initializationError = error
    this.storage = null
    void this.dbp.then(db => db?.close()).catch(() => {})
    this.logError('Persisted storage disabled after initialization failure.', {
      databaseName: this.databaseName,
      error: this.describeError(error)
    })
  }

  /**
   * Returns the configured object stores currently present in IndexedDB.
   * @returns {Promise<string[]>}
   */
  private async getObjectStoreNames(): Promise<string[]> {
    return this.dbp.then(db =>
      Array.from(db.objectStoreNames).filter(storeName => PersistState.hasConfiguredStoreName(storeName))
    )
  }

  /**
   * Repairs corrupted configured stores that are missing metadata or have inconsistent entity metadata.
   * @param {IDBDatabase} db
   * @returns {Promise<void>}
   */
  private async ensureConfiguredStoreMetadata(db: IDBDatabase): Promise<void> {
    const storeNames = Array.from(db.objectStoreNames).filter(storeName =>
      PersistState.hasConfiguredStoreName(storeName)
    )
    const metaRows = await this.readMetaRowsFromDatabase(db, storeNames)

    for (const storeName of storeNames) {
      await this.ensureStoreMetadata(db, storeName, metaRows.get(storeName))
    }
  }

  /**
   * Ensures one configured store has a metadata row.
   * @param {IDBDatabase} db
   * @param {string} storeName
   * @param {unknown} metaRow The store's current metadata row.
   * @returns {Promise<void>}
   */
  private async ensureStoreMetadata(db: IDBDatabase, storeName: string, metaRow: unknown): Promise<void> {
    const configuredStore = PersistState.getConfiguredStoreDefinition(storeName)
    const requiresEntityMetadataRepair =
      configuredStore?.type === 'entity' &&
      isEmptyPersistedMetaRow(metaRow) &&
      Object.keys(await readObjectStoreRows(db, storeName)).some(rowKey => rowKey !== PERSIST_STATE_META_ROW_KEY)

    if (metaRow !== undefined && !requiresEntityMetadataRepair) {
      return
    }

    await this.writeMetaRow(db, storeName, await this.createMissingMetaRow(db, storeName))
  }

  /**
   * Reads the metadata rows of the given stores in one transaction.
   * Startup waits for this check, so one transaction for all stores keeps it short.
   * @param {IDBDatabase} db
   * @param {string[]} storeNames
   * @returns {Promise<Map<string, unknown>>}
   */
  private readMetaRowsFromDatabase(db: IDBDatabase, storeNames: string[]): Promise<Map<string, unknown>> {
    if (!storeNames.length) {
      return Promise.resolve(new Map())
    }

    return new Promise<Map<string, unknown>>((resolve, reject) => {
      const transaction = db.transaction(storeNames, 'readonly')
      const requests = storeNames.map(
        storeName => [storeName, transaction.objectStore(storeName).get(PERSIST_STATE_META_ROW_KEY)] as const
      )

      transaction.oncomplete = () =>
        resolve(new Map(requests.map(([storeName, request]) => [storeName, request.result])))
      // A failed request aborts the transaction. `transaction.error` is set by then, but not yet in `onerror`.
      transaction.onabort = () => reject(transaction.error)
    })
  }

  /**
   * Creates a replacement metadata row for stores that currently have none.
   * Uses the configured store type from the active schema.
   * @param {IDBDatabase} _db
   * @param {string} storeName
   * @returns {Promise<PersistedEntityMeta | PersistedPlainStateMeta | Record<string, never>>}
   */
  private async createMissingMetaRow(
    _db: IDBDatabase,
    storeName: string
  ): Promise<PersistedEntityMeta | PersistedPlainStateMeta | Record<string, never>> {
    const configuredStore = PersistState.getConfiguredStoreDefinition(storeName)

    if (configuredStore?.type === 'entity') {
      return this.createDefaultEntityMeta()
    }

    if (configuredStore?.type === 'plain') {
      return this.createDefaultPlainMeta()
    }

    throw new Error(`IndexedDB object store "${storeName}" is missing a configured persistence type.`)
  }

  /**
   * Creates the default metadata row for entity stores.
   * @returns {PersistedEntityMeta}
   */
  private createDefaultEntityMeta(): PersistedEntityMeta {
    return {
      active: null,
      loading: false,
      error: null,
      lastSync: null,
      loadedOn: null,
      syncFilters: []
    }
  }

  /**
   * Creates the default metadata row for plain stores.
   * @returns {PersistedPlainStateMeta}
   */
  private createDefaultPlainMeta(): PersistedPlainStateMeta {
    return {
      loading: true,
      error: null,
      loadedOn: null
    }
  }

  /**
   * Writes a metadata row into a configured store.
   * @param {IDBDatabase} db
   * @param {string} storeName
   * @param {unknown} metaRow
   * @returns {Promise<void>}
   */
  private writeMetaRow(db: IDBDatabase, storeName: string, metaRow: unknown): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(storeName, 'readwrite')
      const objectStore = transaction.objectStore(storeName)

      objectStore.put(metaRow, PERSIST_STATE_META_ROW_KEY)

      transaction.oncomplete = () => resolve()
      transaction.onabort = transaction.onerror = () => reject(transaction.error)
    })
  }

  // Plain-store helpers

  /**
   * Reads all rows from a dedicated plain-store object store.
   * @param {string} storeName
   * @returns {Promise<Record<string, unknown>>}
   */
  private async readPlainRows(storeName: string): Promise<Record<string, unknown>> {
    return this.dbp.then(db => {
      if (!db.objectStoreNames.contains(storeName)) {
        return {}
      }

      return readObjectStoreRows(db, storeName)
    })
  }

  /**
   * Reconstructs a plain persisted store from its dedicated object-store rows.
   * @param {string} storeName
   * @returns {Promise<T | undefined>}
   */
  private async readPlainState<T>(storeName: string): Promise<T | undefined> {
    return this.readPlainRows(storeName).then(rows => {
      if (!Object.keys(rows).length || this.hasOnlyEmptyMetaRow(rows)) {
        return undefined
      }

      return this.normalizePersistedPlainState(unpackPlainStateRows<T>(rows))
    })
  }

  /**
   * Normalizes persisted plain-store metadata after reading it from IndexedDB.
   * @param {T | undefined} persistedState
   * @returns {T | undefined}
   */
  private normalizePersistedPlainState<T>(persistedState: T | undefined): T | undefined {
    if (!persistedState || typeof persistedState !== 'object') {
      return persistedState
    }

    const state = persistedState as T & { loadedOn?: Date | string | null }

    return {
      ...state,
      loadedOn: normalizeDateValue(state.loadedOn)
    }
  }

  /**
   * Persists a plain signal-store snapshot as one row per property plus the metadata row.
   * @param {string} storeName
   * @param {unknown} value
   * @returns {Promise<void>}
   */
  private async writePlainState(storeName: string, value: unknown): Promise<void> {
    const nextRows = packPlainStateRows(value)
    const previousRows = await this.readPlainRows(storeName)
    const upserts = Object.entries(nextRows).reduce<Record<string, unknown>>((changedRows, [rowKey, rowValue]) => {
      if (previousRows[rowKey] === rowValue) {
        return changedRows
      }

      changedRows[rowKey] = rowValue

      return changedRows
    }, {})
    const deletes = Object.keys(previousRows).filter(
      rowKey => !(rowKey in nextRows) && rowKey !== PERSIST_STATE_META_ROW_KEY
    )
    const meta =
      PERSIST_STATE_META_ROW_KEY in upserts
        ? (upserts[PERSIST_STATE_META_ROW_KEY] as PersistedPlainStateMeta)
        : undefined

    delete upserts[PERSIST_STATE_META_ROW_KEY]

    return this.applyPlainStateChangesInternal(storeName, {
      upserts,
      deletes,
      meta
    })
  }

  /**
   * Applies row-level plain-store changes in a single transaction.
   * @param {string} storeName
   * @param {PlainStateChanges} changes
   * @returns {Promise<void>}
   */
  private async applyPlainStateChangesInternal(storeName: string, changes: PlainStateChanges): Promise<void> {
    const upserts = changes.upserts || {}
    const deletes = [...new Set((changes.deletes || []).filter(Boolean))]

    return this.dbp.then(
      db =>
        new Promise<void>((resolve, reject) => {
          if (!db.objectStoreNames.contains(storeName)) {
            const error = this.createMissingObjectStoreError(storeName, db)

            this.logWriteErrorOnce('plain', storeName, db.name, error)
            reject(error)

            return
          }

          const transaction = db.transaction(storeName, 'readwrite')
          const objectStore = transaction.objectStore(storeName)

          if (changes.meta) {
            objectStore.put(changes.meta, PERSIST_STATE_META_ROW_KEY)
          }

          Object.entries(upserts).forEach(([propertyKey, propertyValue]) => {
            objectStore.put(propertyValue, propertyKey)
          })

          deletes.forEach(propertyKey => {
            objectStore.delete(propertyKey)
          })

          transaction.oncomplete = () => resolve()
          transaction.onabort = transaction.onerror = () => reject(transaction.error)
        })
    )
  }

  // Entity-store helpers

  /**
   * Reads the metadata row and all entity rows of an entity store in one transaction.
   * `getAll` reads each key range in one request. A cursor needs one task per row, and each task waits behind
   * other startup work, so large stores (for example 13k companies) restored slowly while the app started.
   * @param {string} storeName
   * @returns {Promise<{ meta: PersistedEntityMeta | undefined; entities: Entity[] }>}
   */
  private async readEntityStore<Entity extends { id: string }>(
    storeName: string
  ): Promise<{ meta: PersistedEntityMeta | undefined; entities: Entity[] }> {
    return this.dbp.then(db => {
      if (!db.objectStoreNames.contains(storeName)) {
        return { meta: undefined, entities: [] }
      }

      return new Promise<{ meta: PersistedEntityMeta | undefined; entities: Entity[] }>((resolve, reject) => {
        const transaction = db.transaction(storeName, 'readonly')
        const objectStore = transaction.objectStore(storeName)
        const metaRequest = objectStore.get(PERSIST_STATE_META_ROW_KEY)
        const rowRequests = createRowRangesWithoutMeta().map(range => objectStore.getAll(range))

        transaction.oncomplete = () =>
          resolve({
            meta: metaRequest.result as PersistedEntityMeta | undefined,
            entities: rowRequests.flatMap(request => request.result as Entity[])
          })
        // A failed request aborts the transaction. `transaction.error` is set by then, but not yet in `onerror`.
        transaction.onabort = () => reject(transaction.error)
      })
    })
  }

  /**
   * Reconstructs the entity-store runtime shape from dedicated rows.
   * @param {PersistedEntityMeta | undefined} meta
   * @param {Entity[]} entities
   * @returns {PersistedEntityState<Entity> | undefined}
   */
  private buildPersistedEntityState<Entity extends { id: string }>(
    meta: PersistedEntityMeta | undefined,
    entities: Entity[]
  ): PersistedEntityState<Entity> | undefined {
    const persistedMeta = this.isEmptyMetaRow(meta) ? undefined : meta

    if (!persistedMeta && !entities.length) {
      return undefined
    }

    return {
      entities,
      ids: entities.map(entity => entity.id),
      active: persistedMeta?.active || null,
      loading: persistedMeta?.loading ?? false,
      error: persistedMeta?.error || null,
      lastSync: normalizeDateValue(persistedMeta?.lastSync),
      loadedOn: normalizeDateValue(persistedMeta?.loadedOn),
      syncFilters: normalizeSyncFilters(persistedMeta?.syncFilters)
    }
  }

  /**
   * Applies entity-store row changes in a single transaction.
   * @param {string} storeName
   * @param {EntityChanges<Entity>} changes
   * @returns {Promise<void>}
   */
  private async applyEntityChangesInternal<Entity extends { id: string }>(
    storeName: string,
    changes: EntityChanges<Entity>
  ): Promise<void> {
    const upserts = changes.upserts || []
    const deletes = [...new Set((changes.deletes || []).filter(Boolean))]

    return this.dbp.then(
      db =>
        new Promise<void>((resolve, reject) => {
          if (!db.objectStoreNames.contains(storeName)) {
            const error = this.createMissingObjectStoreError(storeName, db)

            this.logWriteErrorOnce('entity', storeName, db.name, error)
            reject(error)

            return
          }

          const transaction = db.transaction(storeName, 'readwrite')
          const objectStore = transaction.objectStore(storeName)

          objectStore.put(changes.meta, PERSIST_STATE_META_ROW_KEY)

          upserts.forEach((entity: { id?: string }) => {
            if (entity?.id) {
              objectStore.put(entity, entity.id)
            }
          })

          deletes.forEach(entityId => {
            objectStore.delete(entityId)
          })

          transaction.oncomplete = () => resolve()
          transaction.onabort = transaction.onerror = () => reject(transaction.error)
        })
    )
  }

  // Shared maintenance helpers

  /**
   * Determines whether a persisted store should be deleted for a given command timestamp.
   * @param {unknown} storedValue
   * @param {Date} commandDate
   * @returns {boolean}
   */
  private shouldDeleteByDate(storedValue: unknown, commandDate: Date): boolean {
    if (storedValue instanceof Date) {
      return commandDate.getTime() > storedValue.getTime()
    }

    if (!storedValue || typeof storedValue !== 'object') {
      return false
    }

    const loadedOn = normalizeDateValue(
      (storedValue as { loadedOn?: Date | string | null; __loadedOn?: Date | string | null }).loadedOn ||
        (storedValue as { __loadedOn?: Date | string | null }).__loadedOn
    )

    return !!loadedOn && commandDate.getTime() > loadedOn.getTime()
  }

  /**
   * Deletes a dedicated object store when its metadata qualifies for deletion.
   * @param {string} storeName
   * @param {Date} commandDate
   * @returns {Promise<boolean>}
   */
  private async deleteStoreInternal(storeName: string, commandDate?: Date): Promise<boolean> {
    return this.dbp
      .then(db => this.deleteStoreFromDatabase(db, storeName, commandDate))
      .catch(error => {
        if ((error as { name?: string })?.name !== 'InvalidStateError') {
          throw error
        }

        this.dbp = this.openDatabase()

        return this.dbp.then(db => this.deleteStoreFromDatabase(db, storeName, commandDate))
      })
  }

  /**
   * Deletes one persisted store using an open database connection.
   * @param {IDBDatabase} db Database connection.
   * @param {string} storeName Store name.
   * @param {Date} commandDate Optional deletion cutoff.
   * @returns {Promise<boolean>} Whether rows were deleted.
   */
  private deleteStoreFromDatabase(db: IDBDatabase, storeName: string, commandDate?: Date): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      if (!db.objectStoreNames.contains(storeName)) {
        resolve(false)

        return
      }

      try {
        const transaction = db.transaction(storeName, 'readwrite')
        const objectStore = transaction.objectStore(storeName)
        const countRequest = objectStore.count()
        const metaRequest = objectStore.get(PERSIST_STATE_META_ROW_KEY)
        let rowCount = 0
        let metaValue: unknown
        let countReady = false
        let metaReady = false
        let deleted = false

        const maybeDelete = () => {
          if (!countReady || !metaReady) {
            return
          }

          const shouldDelete = commandDate ? this.shouldDeleteByDate(metaValue, commandDate) : rowCount > 0
          const hasPersistedRows = rowCount > 1 || !this.isEmptyMetaRow(metaValue)

          if (shouldDelete && hasPersistedRows) {
            objectStore.clear()
            objectStore.put(PERSIST_STATE_EMPTY_META_ROW, PERSIST_STATE_META_ROW_KEY)
            deleted = true
          }
        }

        countRequest.onsuccess = () => {
          rowCount = countRequest.result
          countReady = true
          maybeDelete()
        }

        metaRequest.onsuccess = () => {
          metaValue = metaRequest.result
          metaReady = true
          maybeDelete()
        }

        countRequest.onerror = () => reject(countRequest.error)
        metaRequest.onerror = () => reject(metaRequest.error)
        transaction.oncomplete = () => resolve(deleted)
        transaction.onabort = transaction.onerror = () => reject(transaction.error)
      } catch (error) {
        reject(error)
      }
    })
  }

  /**
   * Deletes multiple dedicated object stores.
   * @param {string[]} storeNames
   * @param {Date} commandDate
   * @returns {Promise<boolean>}
   */
  private async deleteStoresInternal(storeNames: string[], commandDate?: Date): Promise<boolean> {
    let deleted = false

    for (const storeName of [...new Set((storeNames || []).filter(Boolean))]) {
      deleted = (await this.deleteStoreInternal(storeName, commandDate)) || deleted
    }

    return deleted
  }

  /**
   * Deletes all configured dedicated object stores.
   * @param {Date} commandDate
   * @returns {Promise<boolean>}
   */
  private async deleteAllStoresInternal(commandDate?: Date): Promise<boolean> {
    const storeNames = await this.getObjectStoreNames()
    let deleted = false

    for (const storeName of storeNames) {
      deleted = (await this.deleteStoreInternal(storeName, commandDate)) || deleted
    }

    return deleted
  }

  /**
   * Clears all rows from all configured dedicated object stores.
   * @returns {Promise<void>}
   */
  private async clearAllStores(): Promise<void> {
    const storeNames = await this.getObjectStoreNames()
    await Promise.all(storeNames.map(storeName => this.deleteStoreInternal(storeName).then(() => void 0)))
  }

  /**
   * Builds a JSON-compatible dump for all configured dedicated object stores.
   * @param {IDBDatabase} db
   * @returns {Promise<Record<string, Record<string, unknown>>>}
   */
  private async buildDumpData(db: IDBDatabase): Promise<Record<string, Record<string, unknown>>> {
    const dump: Record<string, Record<string, unknown>> = {}
    const logicalStoreNames = Array.from(db.objectStoreNames).filter(storeName =>
      PersistState.hasConfiguredStoreName(storeName)
    )

    for (const storeName of logicalStoreNames) {
      const rows = await readObjectStoreRows(db, storeName)

      if (Object.keys(rows).length) {
        dump[storeName] = rows
      }
    }

    return dump
  }

  /**
   * Determines whether a store only contains the empty initialization metadata row.
   * @param {Record<string, unknown>} rows
   * @returns {boolean}
   */
  private hasOnlyEmptyMetaRow(rows: Record<string, unknown>): boolean {
    return Object.keys(rows).length === 1 && this.isEmptyMetaRow(rows[PERSIST_STATE_META_ROW_KEY])
  }

  /**
   * Determines whether a metadata row is the empty initialization marker.
   * @param {unknown} metaRow
   * @returns {boolean}
   */
  private isEmptyMetaRow(metaRow: unknown): boolean {
    return isEmptyPersistedMetaRow(metaRow)
  }

  /**
   * Handles broken IndexedDB reads by resetting the database.
   * @param {unknown} error
   */
  private handleReadError(error: unknown): void {
    if ((error as { name?: string })?.name === 'NotFoundError') {
      this.logError('IndexedDB read failed with NotFoundError. Deleting the database and reloading.', {
        databaseName: this.databaseName,
        error: this.describeError(error)
      })
      window.indexedDB.deleteDatabase(this.databaseName)
      location.reload()
    }
  }

  /**
   * Determines whether persistence currently runs on a recovery database name.
   * @returns {boolean}
   */
  private isUsingRecoveryDatabase(): boolean {
    return this.databaseName !== this.primaryDatabaseName
  }

  /**
   * Restores persistence back to the primary IndexedDB database name when a recovery database is active.
   * @param {IDBDatabase} activeDb
   * @returns {Promise<void>}
   */
  private async tryRestorePrimaryDatabaseIfNeeded(activeDb: IDBDatabase): Promise<void> {
    if (!this.isUsingRecoveryDatabase()) {
      return
    }

    const recoveryDatabaseName = this.databaseName
    const primaryDatabaseName = this.primaryDatabaseName
    let primaryDb: IDBDatabase | null = null

    try {
      const persistedData = await exportPersistedData(activeDb, PersistState.getTestStoreDefinitions())

      await this.deleteDatabase(primaryDatabaseName)
      primaryDb = await this.openCleanDatabase(primaryDatabaseName, false)
      await importPersistedData(primaryDb, persistedData, PersistState.getConfiguredStoreNames())
      await this.ensureConfiguredStoreMetadata(primaryDb)

      this.databaseName = primaryDatabaseName
      this.dbp = Promise.resolve(primaryDb)
      PersistState.setPersistedDatabaseNameOverride(null)
    } catch (error) {
      primaryDb?.close()
      this.databaseName = recoveryDatabaseName
      this.dbp = Promise.resolve(activeDb)
      PersistState.setPersistedDatabaseNameOverride(recoveryDatabaseName)
      this.logError(
        'Restoring persistence to the primary IndexedDB database name failed. Continuing with the recovery database.',
        {
          recoveryDatabaseName,
          primaryDatabaseName,
          error: this.describeError(error)
        }
      )

      return
    }

    activeDb.close()

    try {
      await this.deleteDatabase(recoveryDatabaseName)
    } catch (error) {
      this.logError('Deleting the recovery IndexedDB database after restoring the primary database name failed.', {
        recoveryDatabaseName,
        primaryDatabaseName,
        error: this.describeError(error)
      })
    }
  }

  /**
   * Deletes the broken IndexedDB database and recreates a clean version-5 schema.
   * @param {unknown} error
   * @returns {Promise<boolean>}
   */
  private async tryRecoverInitializationFailure(error: unknown): Promise<boolean> {
    try {
      void this.dbp.then(db => db?.close()).catch(() => {})
      await this.deleteDatabase(this.databaseName)
      await this.openCleanDatabase(this.databaseName)
      PersistState.setPersistedDatabaseNameOverride(
        this.databaseName === this.primaryDatabaseName ? null : this.databaseName
      )

      return true
    } catch (recoveryError) {
      this.logError('Destructive IndexedDB recovery failed.', {
        databaseName: this.databaseName,
        initialError: this.describeError(error),
        recoveryError: this.describeError(recoveryError)
      })
    }

    return this.tryRecoverWithAlternateDatabase(error)
  }

  /**
   * Switches persistence to a fresh alternate database name when the current one remains wedged.
   * @param {unknown} error
   * @returns {Promise<boolean>}
   */
  private async tryRecoverWithAlternateDatabase(error: unknown): Promise<boolean> {
    const alternateDatabaseName = PersistState.createRecoveryDatabaseName(this.primaryDatabaseName)

    try {
      this.databaseName = alternateDatabaseName
      PersistState.setPersistedDatabaseNameOverride(alternateDatabaseName)
      await this.openCleanDatabase(alternateDatabaseName)

      return true
    } catch (alternateRecoveryError) {
      PersistState.setPersistedDatabaseNameOverride(null)
      this.databaseName = this.primaryDatabaseName
      this.logError('Alternate IndexedDB recovery failed.', {
        alternateDatabaseName,
        error: this.describeError(alternateRecoveryError)
      })

      return false
    }
  }

  /**
   * Opens a clean version-5 database and ensures all configured metadata rows exist.
   * @param {string} databaseName
   * @param {boolean} [activateDatabase=true]
   * @returns {Promise<IDBDatabase>}
   */
  private async openCleanDatabase(databaseName: string, activateDatabase = true): Promise<IDBDatabase> {
    const rebuiltDatabasePromise = this.openDatabaseByName(databaseName, PERSIST_STATE_DB_VERSION)

    if (activateDatabase) {
      this.dbp = rebuiltDatabasePromise
    }

    const rebuiltDb = await rebuiltDatabasePromise

    await this.ensureConfiguredStoreMetadata(rebuiltDb)

    return rebuiltDb
  }

  /**
   * Logs restore timing when `awDebug` is enabled.
   * @param {string} key
   * @param {number} t0
   */
  private logRestore(key: string, t0: number): void {
    if (window['awDebug']) {
      console.log(`Store restored - ${key}: ${Math.round(performance.now() - t0)} ms.`)
    }
  }

  /**
   * Logs persist timing when `awDebug` is enabled.
   * @param {string} key
   * @param {number} t0
   * @param {string} details
   */
  private logPersist(key: string, t0: number, details?: string): void {
    if (!window['awDebug']) {
      return
    }

    const suffix = details ? ` (${details})` : ''

    console.log(`Store persisted - ${key}: ${Math.round(performance.now() - t0)} ms${suffix}.`)
  }

  /**
   * Creates an actionable error for persistence requests targeting a missing IndexedDB object store.
   * @param {string} storeName
   * @param {IDBDatabase} db
   * @returns {Error}
   */
  private createMissingObjectStoreError(storeName: string, db: IDBDatabase): Error {
    if (!PersistState.hasConfiguredStoreName(storeName)) {
      return new Error(
        `IndexedDB object store "${storeName}" is missing because it is not registered in APP_PERSISTED_STORES. Add it to the persisted-store schema and bump PERSIST_STATE_DB_VERSION before persisting it.`
      )
    }

    return new Error(
      `IndexedDB object store "${storeName}" is missing from database "${db.name}" although it is configured in APP_PERSISTED_STORES. Recreate the database with the current schema before persisting it.`
    )
  }

  /**
   * Logs a store-write error only once until the store persists successfully again.
   * @param {'plain' | 'entity'} kind
   * @param {string} storeName
   * @param {string} databaseName
   * @param {Error} error
   */
  private logWriteErrorOnce(kind: 'plain' | 'entity', storeName: string, databaseName: string, error: Error): void {
    const errorKey = `${kind}:${storeName}:${error.message}`

    if (this.loggedWriteErrors.has(errorKey)) {
      return
    }

    this.loggedWriteErrors.add(errorKey)

    this.logError(`Persisting ${kind} signal-store state failed.`, {
      storeName,
      databaseName,
      error: this.describeError(error)
    })
  }

  /**
   * Logs actual persistence errors.
   * @param {string} message
   * @param {unknown} details
   */
  private logError(message: string, details?: unknown): void {
    if (details === undefined) {
      console.error(`[PersistState] ${message}`)

      return
    }

    console.error(`[PersistState] ${message}`, details)
  }

  /**
   * Normalizes unknown IndexedDB errors for logging.
   * @param {unknown} error
   * @returns {unknown}
   */
  private describeError(error: unknown): unknown {
    if (error instanceof DOMException || error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack
      }
    }

    if (error && typeof error === 'object') {
      return error
    }

    return { value: error }
  }
}
